import { useMutation, useQuery } from '@apollo/client'
import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import I18n from 'i18next'
import { Column } from 'material-table'
import { useSnackbar } from 'notistack'
import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import GraphqlError from '../../components/GraphqlError'
import Loading from '../../components/Loading'
import MediaField from '../../components/files/MediaField'
import { MediaFieldView } from '../../components/files/MediaFieldView'
import { AclTable } from '../../components/table/AclTable'
import {
  MediaDocumentTypeDataFragment,
  MediaDocumentTypeInput,
  mediaDocumentTypesQuery,
  mediaDocumentTypesQueryVariables,
  MutateMediaDocumentTypeMutation,
  MutateMediaDocumentTypeMutationVariables,
} from '../../generated/graphql'
import DH from '../../helpers/DH'
import DataHelper, { EntityNames } from '../../helpers/DataHelper'
import { useDeleteMutation } from '../../helpers/GqlHelper'
import { slugify } from '../../helpers/Helpers'
import { requiredField } from '../../helpers/validations'
import { useBranchFilteredQuery } from '../../hooks/BranchFilterHook'
import Medias from '../../schema/Medias'

type DocumentTypeRow = MediaDocumentTypeDataFragment & {
  defaultMediaId?: string | null
}

function getCodeValidationError(
  rowData: Pick<DocumentTypeRow, 'id' | 'code'>,
  documentTypes: MediaDocumentTypeDataFragment[],
): string | undefined {
  const code = (rowData.code || '').trim()
  if (!code) {
    return undefined
  }
  if (documentTypes.find((d) => d.id !== rowData.id && slugify(code) === d.code)) {
    return I18n.t('Code already exists')
  }
  return undefined
}

function uniqueCode(documentTypes: MediaDocumentTypeDataFragment[]) {
  return (rowData) => getCodeValidationError(rowData, documentTypes) ?? true
}

export function DocumentCategoriesPanel() {
  const { t } = useTranslation()
  const { enqueueSnackbar, closeSnackbar } = useSnackbar()
  const branchFilter = useBranchFilteredQuery()
  const [deleteMediaDocumentType] = useDeleteMutation(Medias.GET_DOCUMENT_TYPES, branchFilter.variables)
  const [mutateMediaDocumentType] = useMutation<
    MutateMediaDocumentTypeMutation,
    MutateMediaDocumentTypeMutationVariables
  >(Medias.MUTATE_DOCUMENT_TYPE, {
    refetchQueries: [{ query: Medias.GET_DOCUMENT_TYPES, variables: { ...branchFilter?.variables } }],
  })
  const { loading, error, data } = useQuery<mediaDocumentTypesQuery, mediaDocumentTypesQueryVariables>(
    Medias.GET_DOCUMENT_TYPES,
    branchFilter,
  )

  const [newDocumentTypeAdded, setNewDocumentTypeAdded] = useState(null)
  const allDocumentTypes = data?.mediaDocumentTypes
  const tableData: DocumentTypeRow[] = useMemo(() => {
    return (
      allDocumentTypes?.map((docType) => ({
        ...docType,
        defaultMediaId: docType.defaultMedia?.id,
      })) || []
    )
  }, [allDocumentTypes])

  const columns: Column<DocumentTypeRow>[] = useMemo(
    () => [
      {
        title: t('Name'),
        field: 'name',
        validate: requiredField('name'),
        editComponent: (editProps) => {
          return (
            <TextField
              variant="standard"
              value={editProps.value || ''}
              label={t('Name')}
              fullWidth
              onChange={(val) => {
                editProps.onChange(val.target.value)
              }}
            />
          )
        },
      },
      {
        title: t('Code'),
        field: 'code',
        validate: uniqueCode(allDocumentTypes || []),
        editComponent: (editProps) => {
          const codeHelperText = t(
            'Used in email template variables. Auto-generated from name if empty, with a v2, v3 suffix when needed.',
          )
          const codeError = getCodeValidationError(
            { ...editProps.rowData, code: editProps.value },
            allDocumentTypes || [],
          )
          return (
            <TextField
              variant="standard"
              value={editProps.value || ''}
              label={t('Code')}
              helperText={codeError || codeHelperText}
              error={!!codeError}
              fullWidth
              disabled={!!editProps.rowData.id}
              onChange={(val) => {
                editProps.onChange(val.target.value)
              }}
            />
          )
        },
      },
      {
        title: t('Default file'),
        field: 'defaultMediaId',
        editComponent: (editProps) => {
          return (
            <MediaField
              idMedia={editProps.value || undefined}
              notDeleteMedia={true}
              uploadAsGlobal={true}
              showGallery={false}
              onChange={(id) => {
                editProps.onChange(id || null)
              }}
            />
          )
        },
        render: (nodeData) => {
          if (!nodeData.defaultMediaId) {
            return t('No file')
          }
          const preloadedMedia = nodeData.defaultMedia
            ? {
                filename: nodeData.defaultMedia.filename,
                mimeType: nodeData.defaultMedia.mimeType,
                name: nodeData.defaultMedia.name,
              }
            : undefined
          return <MediaFieldView idMedia={nodeData.defaultMediaId} mediaData={preloadedMedia} />
        },
      },
    ],
    [allDocumentTypes, t],
  )

  const deleteNode = async (id: string) => {
    try {
      await deleteMediaDocumentType({ variables: { input: { id } } })
    } catch (errorDelete) {
      let messageError: string
      if (errorDelete.message.includes('ER_ROW_IS_REFERENCED_2')) {
        messageError = t('This document type cannot be deleted because it is being used in some document.')
      } else {
        messageError = t('Cannot delete this document type.')
      }
      enqueueSnackbar(messageError, {
        variant: 'error',
        persist: true,
        action: (key) => (
          <Button variant="text" color="inherit" onClick={() => closeSnackbar(key)}>
            {t('Close')}
          </Button>
        ),
      })
    }
  }

  const mutateDocumentType = async (docType: DocumentTypeRow) => {
    try {
      const input: MediaDocumentTypeInput = DataHelper.cleanInputData({
        id: docType.id,
        name: docType.name,
        code: docType.code,
        ...(docType.defaultMediaId !== undefined && { defaultMediaId: docType.defaultMediaId || null }),
      })
      const response = await mutateMediaDocumentType({
        variables: { input },
      })
      setNewDocumentTypeAdded(response.data.mutateMediaDocumentType)
      return true
    } catch (errorMutate) {
      const messages = DH.getGraphqlErrorMessages(errorMutate)
      const message = messages.some((m) => m.includes('ER_DUP_ENTRY'))
        ? t('Code already exists')
        : messages.join(', ')
      enqueueSnackbar(message, { variant: 'error' })
      throw errorMutate
    }
  }

  if (error) {
    return <GraphqlError error={error} />
  }
  if (loading || !data) {
    return <Loading />
  }

  return (
    <div>
      {!allDocumentTypes?.length && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t('No documents yet. Add your first document using the + button below.')}
        </Alert>
      )}
      <AclTable<DocumentTypeRow>
        entityName={EntityNames.MediaDocumentType}
        title={t('Documents')}
        columns={columns}
        options={{
          showTitle: false,
        }}
        localization={{
          body: { editRow: { deleteText: t('Are you sure you want to delete this document?') } },
        }}
        data={tableData}
        paginateToElement={newDocumentTypeAdded}
        editable={{
          onRowAdd: async (newData) => {
            await mutateDocumentType(newData)
          },
          onRowUpdate: async (newData) => {
            await mutateDocumentType(newData)
          },
          onRowDelete: async (oldData) => {
            await deleteNode(oldData.id)
          },
        }}
      />
    </div>
  )
}
